import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { firstAvailable } from '@zerops/zef/core';
import {
  onWebsocketMessageDispatchAddRemoveEntities,
  onWebsocketMessageDispatchUpdateEntities
} from '@zerops/zef/entities';
import { ZefSnackService } from '@zerops/zef/snack';
import { of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import { billingInfo } from '../client-base';
import { PaymentEntity } from '../payment-base';
import { loadCountryList } from '../settings-base';
import { UserEntity } from '../user-base';
import {
  availableAddons,
  BillingBaseActionUnion,
  paymentRequest, paymentRequestFail,
  paymentRequestSuccess,
  paymentSources,
  paymentSourcesFail,
  paymentSourcesSuccess,
  availableAddonsFail,
  availableAddonsSuccess,
  removePaymentSource,
  removePaymentSourceSuccess,
  removePaymentSourceFail,
  confirmPayment,
  confirmPaymentCancel,
  autoTopUpUpdate,
  autoTopUpUpdateSuccess,
  autoTopUpUpdateFail
} from './billing-base.action';
import { BillingBaseApi } from './billing-base.api';
import { zefRemoveProgress } from '@zerops/zef/progress';
import { zefRemoveError } from '@zerops/zef/errors';
import { canViewFinances } from '@zerops/models/client-user';

@Injectable()
export class BillingBaseEffect {
  // # Deps
  #snack = inject(ZefSnackService);
  #userEntity = inject(UserEntity);
  #paymentEntity = inject(PaymentEntity);
  #actions$ = inject<Actions<BillingBaseActionUnion>>(Actions);
  #api = inject(BillingBaseApi);

  // # Streams
  #activeClientId$ = this.#userEntity.activeClientId$.pipe(filter((d) => !!d));

  onPaymentRequestInfo$ = createEffect(() => this.#actions$.pipe(
    ofType(paymentRequest),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .paymentRequest$(
          action.data,
          clientId
        )
        .pipe(
          map((res) => paymentRequestSuccess(res, action)),
          catchError((err) => of(paymentRequestFail(err, action)))
        )
      )
    )
  )));

  onPaymentRequestInfoFailCancelConfirmPayment$ = createEffect(() => this.#actions$.pipe(
    ofType(paymentRequestFail),
    map(() => confirmPaymentCancel())
  ));

  onConfirmPaymentCancel$ = createEffect(() => this.#actions$.pipe(
    ofType(confirmPaymentCancel),
    delay(0),
    mergeMap(() => [
      zefRemoveError(confirmPayment.type),
      zefRemoveProgress(confirmPayment.type)
    ])
  ));

  onRemovePaymentSource$ = createEffect(() => this.#actions$.pipe(
    ofType(removePaymentSource),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .removePaymentSource$(clientId, action.data)
        .pipe(
          map((res) => removePaymentSourceSuccess(res, action)),
          catchError((err) => of(removePaymentSourceFail(err, action)))
        )
      )
    ))
  ));

  onRemovePaymentSourceSuccessNotification$ = createEffect(() => this.#actions$.pipe(
    ofType(removePaymentSourceSuccess),
    switchMap(() => this.#snack.success$({ text: 'Card was removed successfully' }))
  ), { dispatch: false });

  onPaymentSources$ = createEffect(() => this.#actions$.pipe(
    ofType(paymentSources),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .paymentSources$(clientId)
        .pipe(
          map((res) => paymentSourcesSuccess(res, action)),
          catchError((err) => of(paymentSourcesFail(err, action)))
        )
      )
    )
  )));

  onActiveClientIdLoadAvailableAddons$ = createEffect(() => this.#activeClientId$.pipe(
     map(() => availableAddons())
  ));

  onAvailableAddonsRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(availableAddons),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .availableAddons$(clientId)
        .pipe(
          map((res) => availableAddonsSuccess(res, action)),
          catchError((err) => of(availableAddonsFail(err, action)))
        )
      )
    )
  )));

  onUpdateAutocharge$ = createEffect(() => this.#actions$.pipe(
    ofType(autoTopUpUpdate),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .autoTopUpUpdate$(clientId, action.data)
        .pipe(
          map((res) => autoTopUpUpdateSuccess(res, action)),
          catchError((err) => of(autoTopUpUpdateFail(err, action)))
        )
      )
    )
  )));

  onUpdateAutochargeSuccessNotification$ = createEffect(() => this.#actions$.pipe(
    ofType(autoTopUpUpdateSuccess),
    switchMap(() => this.#snack.success$({ text: `Auto top-up configuration updated` }))
  ), { dispatch: false });

  onActiveClientIdLoadCountryList$ = createEffect(() => this.#activeClientId$.pipe(
    map(() => loadCountryList())
  ));

  // The billing-info and payment-source GETs allow every role and gate on the flag instead.
  onActiveClientIdLoadBillingInfo$ = createEffect(() => this.#activeClientId$.pipe(
    withLatestFrom(this.#userEntity.activeClientUser$),
    filter(([ _, clientUser ]) => canViewFinances(clientUser)),
    map(() => billingInfo())
  ));

  // payment list / update
  setupPaymentListStream$ = createEffect(() => this.#activeClientId$.pipe(
    withLatestFrom(this.#userEntity.activeClientUser$),
    filter(([ _, clientUser ]) => canViewFinances(clientUser)),
    map(([ clientId ]) => this.#paymentEntity.listSubscribe(
      clientId
    ))
  ));

  onPaymentAddRemoveMessage$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(
      this.#paymentEntity
    )
  ));

  setupUpdateStreamSubscription$ = createEffect(() => this.#activeClientId$.pipe(
    withLatestFrom(this.#userEntity.activeClientUser$),
    filter(([ _, clientUser ]) => canViewFinances(clientUser)),
    map(([ clientId]) => this.#paymentEntity.updateSubscribe(clientId)
  )));

  onUpdateStreamMessage$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this.#paymentEntity)
  ));

}
